import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { TenantConfig, fetchTenantConfig } from '../services/tenantConfigService';

const NOT_FOUND = 'NOT_FOUND';

interface TenantConfigContextType {
  config: TenantConfig | null;
  isLoading: boolean;
  error: string | null;
}

const TenantConfigContext = createContext<TenantConfigContextType | undefined>(undefined);

interface TenantConfigProviderProps {
  children: ReactNode;
}

/**
 * סינון ערכי NOT_FOUND - מחליף ב-fallback סביר כדי שה-UI לא יציג "NOT_FOUND" למשתמש.
 */
const sanitizeConfig = (raw: TenantConfig): TenantConfig => ({
  tenantSlug: raw.tenantSlug,
  systemName: raw.systemName === NOT_FOUND ? '' : raw.systemName,
  systemNameShort: raw.systemNameShort === NOT_FOUND ? '' : raw.systemNameShort,
  logoFilename: raw.logoFilename === NOT_FOUND ? '' : raw.logoFilename,
  companyNameHebrew: raw.companyNameHebrew === NOT_FOUND ? '' : raw.companyNameHebrew,
  // שדות חדשים - fallback למחרוזת ריקה כדי שתצוגה לא תקרוס כשהפרמטר חסר
  welcomeTitle: raw.welcomeTitle === NOT_FOUND ? '' : (raw.welcomeTitle || ''),
  systemTagline: raw.systemTagline === NOT_FOUND ? '' : (raw.systemTagline || ''),
  supportEmail: raw.supportEmail === NOT_FOUND ? '' : (raw.supportEmail || ''),
  supportPhone: raw.supportPhone === NOT_FOUND ? '' : (raw.supportPhone || ''),
  defaultCommissionPercentage: raw.defaultCommissionPercentage || 0,
  // ברירת מחדל false (fail-closed) - אם השרת לא החזיר את הדגל, Cardcom חסום
  cardcomEnabled: raw.cardcomEnabled ?? false,
  // ברירת מחדל false (fail-closed) - אם השרת לא החזיר את הדגל, הוראת קבע בנקאית חסומה
  bankStandingOrderEnabled: raw.bankStandingOrderEnabled ?? false,
  // ברירת מחדל false (fail-closed) - אם השרת לא החזיר את הדגל, מודול ניהול התשלומים חסום
  paymentsManagementEnabled: raw.paymentsManagementEnabled ?? false,
});

export const TenantConfigProvider: React.FC<TenantConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      try {
        const raw = await fetchTenantConfig();
        if (!mounted) return;
        const clean = sanitizeConfig(raw);
        setConfig(clean);

        // Apply config to document
        if (clean.systemName) document.title = clean.systemName;
        // עדכון meta description דינמי per-tenant
        if (clean.systemTagline) {
          let descMeta = document.querySelector('meta[name="description"]');
          if (!descMeta) {
            descMeta = document.createElement('meta');
            descMeta.setAttribute('name', 'description');
            document.head.appendChild(descMeta);
          }
          descMeta.setAttribute('content', clean.systemTagline);
        }
        // עדכון favicon דינמי per-tenant (ה-icon הקשיח ב-index.html תמיד הראה את בזכות)
        if (clean.logoFilename) {
          const logoHref = `/${clean.logoFilename}`;
          const iconLinks = document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"], link[rel="apple-touch-icon"]');
          if (iconLinks.length > 0) {
            iconLinks.forEach((link) => { link.href = logoHref; });
          } else {
            const link = document.createElement('link');
            link.rel = 'icon';
            link.href = logoHref;
            document.head.appendChild(link);
          }
        }
      } catch (err) {
        if (!mounted) return;
        console.error('Failed to load tenant config:', err);
        setError('שגיאה בטעינת הגדרות המערכת');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadConfig();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <TenantConfigContext.Provider value={{ config, isLoading, error }}>
      {children}
    </TenantConfigContext.Provider>
  );
};

export const useTenantConfig = (): TenantConfigContextType => {
  const ctx = useContext(TenantConfigContext);
  if (ctx === undefined) {
    throw new Error('useTenantConfig must be used within a TenantConfigProvider');
  }
  return ctx;
};
